export const GITHUB_URL = 'https://github.com/Fantety/next-engine';
export const BILIBILI_URL = 'https://space.bilibili.com/3546879367317567';
export const RELEASE_TAG = 'v0.1.4.7.1-preview';
export const RELEASE_URL = `${GITHUB_URL}/releases/tag/${RELEASE_TAG}`;
export const RELEASE_DOWNLOAD_URL = `${GITHUB_URL}/releases/download/${RELEASE_TAG}`;
export const FEEDBACK_SURVEY_SRC = 'https://v.wjx.cn/handler/jqemed.ashx?activity=eIvDIES&width=750&source=iframe';

export const siteContent = {
  zh: {
    nav: {
      links: [
        { href: '#start', label: '快速开始' },
        { href: '#features', label: '能力' },
        { href: '#download', label: '下载' },
        { href: '#security', label: '信任' },
        { href: '#build', label: '构建' },
        { href: '#feedback', label: '反馈' },
      ],
      github: 'GitHub',
      language: 'EN',
      languageLabel: 'Switch language to English',
    },
    hero: {
      badge: 'Godot-based / AI-native / Human-in-control',
      title: '让 AI 进入你的游戏开发现场',
      lede:
        'NEXT Engine 基于 Godot 深度定制，把 AI 能力放进编辑器现场。从场景、脚本到资源，AI 可以理解项目上下文、整理任务、协助修改，让你少在工具之间切换，把注意力留给玩法和内容。',
      primary: '快速开始',
      download: '\u4e0b\u8f7d\u9884\u89c8\u7248',
      repository: '查看仓库',
      build: '从源码构建',
      codeLabel: 'clone and build',
      command: 'git clone https://github.com/Fantety/next-engine',
      stats: [
        { value: 'AI Dock', label: '在编辑器里对话、附文件、看任务' },
        { value: 'Project context', label: '理解场景、脚本、资源和工程结构' },
        { value: 'Review flow', label: '重要改动先审查，再决定是否保留' },
      ],
    },
    start: {
      eyebrow: 'Why NEXT Engine',
      title: '少一点切换，多一点创作',
      intro:
        '它不是把 AI 做成另一个聊天窗口，而是让 AI 贴着 Godot 的场景树、脚本、资源和调试节奏工作。',
      cards: [
        {
          icon: 'GitFork',
          title: '看懂你的项目',
          text: '把相关文件、场景、脚本和资源交给 AI Dock，让回复和建议基于真实工程上下文。',
        },
        {
          icon: 'Sparkles',
          title: '把想法推进成改动',
          text: '从玩法草案、脚本调整到 Shader 和文档说明，AI 可以拆任务、给方案、协助落地。',
        },
        {
          icon: 'TerminalSquare',
          title: '结果留给你决定',
          text: '涉及场景、脚本和资源的修改进入审查流程，开发者保留最终判断和回退空间。',
        },
      ],
    },
    features: {
      eyebrow: 'Capabilities',
      title: '把 AI 能力接进编辑器的关键环节',
      intro:
        '从对话、附件、项目上下文到模型和扩展工具，重点是让 AI 能在你已有的 Godot / NEXT Engine 使用方式里帮忙。',
      groups: [
        {
          icon: 'MessageSquare',
          title: 'AI Dock',
          text: '会话、模型、附件、任务列表、流式回复和 Markdown 渲染集中在编辑器侧边栏。',
        },
        {
          icon: 'FileText',
          title: '项目上下文',
          text: '读取文件、搜索文本、理解场景与资源，让回答和修改都基于真实工程上下文。',
        },
        {
          icon: 'Puzzle',
          title: 'MCP 与 Skill',
          text: '通过 MCP、AgentSkill 和规则注入扩展工具链，按需暴露能力。',
        },
        {
          icon: 'Settings',
          title: '模型配置',
          text: '支持 OpenAI-compatible provider、Base URL、API Key、模型 ID 和上下文预算。',
        },
        {
          icon: 'GitPullRequest',
          title: '变更审查',
          text: '场景、脚本和文件修改尽量保持可审查、可确认、可回退。',
        },
        {
          icon: 'BookOpen',
          title: 'Markdown 展示',
          text: '用 MarkdownViewer 展示 AI 回复、项目说明、报告和文档内容，适合编辑器内外复用。',
        },
      ],
    },
    download: {
      eyebrow: 'Preview download',
      title: '下载预览版',
      intro:
        '先体验编辑器、AI 面板和多平台导出链路。这个版本适合试用、演示和验证，不建议直接用于生产项目。',
      versionLabel: 'Release',
      release: RELEASE_TAG,
      releaseMeta: '2026-06-29 发布 / Preview',
      releaseUrl: RELEASE_URL,
      releaseNotes: '查看发布说明',
      editorLabel: '编辑器下载',
      editors: [
        {
          icon: 'Monitor',
          title: 'Windows Editor',
          detail: 'x86_64 · 93.6 MB',
          note: '桌面优先体验',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-windows-editor-x86_64.zip`,
        },
        {
          icon: 'Apple',
          title: 'macOS Editor',
          detail: 'Universal · 163.1 MB',
          note: 'Apple Silicon / Intel',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-macos-editor-universal.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'Android Editor',
          detail: 'ARM64 · 179.2 MB',
          note: '含 HorizonOS / PICO OS 变体',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-editor-arm64.zip`,
        },
      ],
      templatesLabel: 'Export templates',
      templatesTitle: '导出模板与平台包',
      templatesIntro: '同一 release 提供 Android、iOS、Web、Windows 和 macOS 模板，适合验证项目打包链路。',
      templates: [
        {
          icon: 'Smartphone',
          title: 'Android Template ARM64',
          detail: '105.1 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-template-arm64.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'Android Template ARM32',
          detail: '117.5 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-template-arm32.zip`,
        },
        {
          icon: 'Apple',
          title: 'iOS Template ARM64',
          detail: '54.8 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-ios-template-arm64.zip`,
        },
        {
          icon: 'Globe2',
          title: 'Web Template WASM64',
          detail: '31.9 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-web-template-wasm64.zip`,
        },
        {
          icon: 'Globe2',
          title: 'Web Template No Threads',
          detail: '31.3 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-web-template-nothreads-wasm32.zip`,
        },
        {
          icon: 'Monitor',
          title: 'Windows Template',
          detail: 'x86_64 · 39.7 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-windows-template-x86_64.zip`,
        },
        {
          icon: 'Apple',
          title: 'macOS Template',
          detail: 'Universal · 65.8 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-macos-template-universal.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'HorizonOS Editor',
          detail: 'ARM64 · 174.5 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-editor-horizonos-arm64.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'PICO OS Editor',
          detail: 'ARM64 · 174.0 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-editor-picoos-arm64.zip`,
        },
      ],
    },
    security: {
      eyebrow: 'Trust model',
      title: '默认保守的权限和审查边界',
      intro:
        '当 AI 真正参与开发流程时，关键不是让它无所不能，而是让你清楚知道它要做什么、什么时候需要确认、出了问题如何回退。',
      items: [
        {
          icon: 'ShieldCheck',
          title: '权限 Profile',
          text: '工具调用和写入能力按配置启用，高风险行为默认需要明确授权。',
        },
        {
          icon: 'FileSearch',
          title: '引用检查',
          text: '删除或重写资源前检查引用关系，减少误删场景、脚本和资源的风险。',
        },
        {
          icon: 'RotateCcw',
          title: '可回退修改',
          text: '尽量以 diff、审查面板和确认流程呈现 AI 修改，保留开发者最终控制权。',
        },
      ],
    },
    build: {
      eyebrow: 'Build',
      title: '从仓库到本地编辑器',
      intro: '如果你想从源码编译、定位模块或继续开发，可以从这里快速判断主要能力分布在哪里。',
      commandTitle: '构建命令',
      treeTitle: '源码结构',
      commands: [
        '# 日常构建',
        'scons platform=windows',
        '',
        '# 开发构建与测试',
        'scons platform=windows target=editor dev_build=yes tests=yes',
        '',
        '# 运行 AI 相关测试',
        'bin\\next.windows.editor.dev.x86_64.console.exe --test --test-case="*[AgentV1]*"',
      ],
      tree: [
        ['editor/agent_v1/', 'Agent V1 后端：core、domain、session、runtime、tools、permission、MCP、Skill、agents、UI adapter'],
        ['editor/agent_ui/', 'AI Dock、Composer、消息列表、设置页、附件栏和审查面板'],
        ['editor/user_system/', '账号、鉴权、服务端配置和个性化状态'],
        ['core/markdown/', 'MarkdownParser / MarkdownNode 等 core 解析层'],
        ['scene/gui/markdown_viewer*', 'runtime MarkdownViewer 控件、布局、绘制、图片加载和代码高亮 helpers'],
        ['thirdparty/cmark/', 'CommonMark 解析库，供 core/markdown 使用'],
        ['tests/editor/', 'Agent V1、AI UI、用户系统相关测试'],
        ['tests/scene/', 'MarkdownViewer runtime scene 测试'],
      ],
    },
    feedback: {
      title: '把真实体验反馈给 NEXT Engine',
      loading: '问卷加载中...',
      surveySrc: FEEDBACK_SURVEY_SRC,
    },
    footer: {
      text: '基于 Godot 深度定制的 AI 游戏开发编辑器，让 AI 贴着真实项目工作。',
      links: ['快速开始', '能力', '下载', '信任', '构建', '反馈'],
      copyright: '© 2026 Fantety. All rights reserved.',
      bilibili: 'Bilibili',
    },
  },
  en: {
    nav: {
      links: [
        { href: '#start', label: 'Start' },
        { href: '#features', label: 'Features' },
        { href: '#download', label: 'Download' },
        { href: '#security', label: 'Trust' },
        { href: '#build', label: 'Build' },
        { href: '#feedback', label: 'Feedback' },
      ],
      github: 'GitHub',
      language: '中',
      languageLabel: 'Switch language to Chinese',
    },
    hero: {
      badge: 'Godot-based / AI-native / Human-in-control',
      title: 'Bring AI into your game-making flow',
      lede:
        'NEXT Engine is a Godot-based editor that brings AI assistance into the editor itself. From scenes and scripts to assets, it understands project context, organizes tasks, and helps with changes so you can stay focused on gameplay and content.',
      primary: 'Quick start',
      download: 'Download preview',
      repository: 'View repository',
      build: 'Build from source',
      codeLabel: 'clone and build',
      command: 'git clone https://github.com/Fantety/next-engine',
      stats: [
        { value: 'AI Dock', label: 'Chat, attach files, and track tasks in-editor' },
        { value: 'Project context', label: 'Scenes, scripts, assets, and structure' },
        { value: 'Review flow', label: 'Inspect important changes before keeping them' },
      ],
    },
    start: {
      eyebrow: 'Why NEXT Engine',
      title: 'Less switching, more making',
      intro:
        'This is not another detached chat window. AI works beside the scene tree, scripts, assets, and debugging loop you already use.',
      cards: [
        {
          icon: 'GitFork',
          title: 'Understands your project',
          text: 'Attach files, scenes, scripts, and assets to AI Dock so suggestions are grounded in your real project.',
        },
        {
          icon: 'Sparkles',
          title: 'Turns ideas into changes',
          text: 'From gameplay drafts and script edits to shaders and docs, AI can break down tasks and help move them forward.',
        },
        {
          icon: 'TerminalSquare',
          title: 'Keeps you in control',
          text: 'Scene, script, and asset edits can go through review so you decide what lands and what rolls back.',
        },
      ],
    },
    features: {
      eyebrow: 'Capabilities',
      title: 'AI capabilities shaped for the editor',
      intro:
        'Conversation, attachments, project context, model settings, and extension tools are shaped around the Godot / NEXT Engine editing experience.',
      groups: [
        {
          icon: 'MessageSquare',
          title: 'AI Dock',
          text: 'Conversation, model selection, attachments, tasks, streaming output, and Markdown rendering live in the editor sidebar.',
        },
        {
          icon: 'FileText',
          title: 'Project context',
          text: 'Read files, search text, inspect scenes and resources, and ground answers in the actual project.',
        },
        {
          icon: 'Puzzle',
          title: 'MCP and Skills',
          text: 'Extend the toolchain with MCP, AgentSkill routines, and rule injection only when needed.',
        },
        {
          icon: 'Settings',
          title: 'Model settings',
          text: 'Configure OpenAI-compatible providers, Base URL, API keys, model IDs, and context budgets.',
        },
        {
          icon: 'GitPullRequest',
          title: 'Change review',
          text: 'Scene, script, and file edits are designed to be reviewable, confirmable, and reversible.',
        },
        {
          icon: 'BookOpen',
          title: 'Markdown display',
          text: 'MarkdownViewer presents AI answers, project notes, reports, and documentation inside editor and runtime surfaces.',
        },
      ],
    },
    download: {
      eyebrow: 'Preview download',
      title: 'Download the preview build',
      intro:
        'Try the editor, AI panel, and export templates first. This preview is for evaluation and verification, not production projects.',
      versionLabel: 'Release',
      release: RELEASE_TAG,
      releaseMeta: 'Published on June 29, 2026 / Preview',
      releaseUrl: RELEASE_URL,
      releaseNotes: 'Release notes',
      editorLabel: 'Editor downloads',
      editors: [
        {
          icon: 'Monitor',
          title: 'Windows Editor',
          detail: 'x86_64 · 93.6 MB',
          note: 'Best first desktop tryout',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-windows-editor-x86_64.zip`,
        },
        {
          icon: 'Apple',
          title: 'macOS Editor',
          detail: 'Universal · 163.1 MB',
          note: 'Apple Silicon / Intel',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-macos-editor-universal.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'Android Editor',
          detail: 'ARM64 · 179.2 MB',
          note: 'HorizonOS / PICO OS variants available',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-editor-arm64.zip`,
        },
      ],
      templatesLabel: 'Export templates',
      templatesTitle: 'Templates for build validation',
      templatesIntro: 'Android, iOS, Web, Windows, and macOS export templates are available in the same release.',
      templates: [
        {
          icon: 'Smartphone',
          title: 'Android Template ARM64',
          detail: '105.1 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-template-arm64.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'Android Template ARM32',
          detail: '117.5 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-template-arm32.zip`,
        },
        {
          icon: 'Apple',
          title: 'iOS Template ARM64',
          detail: '54.8 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-ios-template-arm64.zip`,
        },
        {
          icon: 'Globe2',
          title: 'Web Template WASM64',
          detail: '31.9 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-web-template-wasm64.zip`,
        },
        {
          icon: 'Globe2',
          title: 'Web Template No Threads',
          detail: '31.3 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-web-template-nothreads-wasm32.zip`,
        },
        {
          icon: 'Monitor',
          title: 'Windows Template',
          detail: 'x86_64 · 39.7 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-windows-template-x86_64.zip`,
        },
        {
          icon: 'Apple',
          title: 'macOS Template',
          detail: 'Universal · 65.8 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-macos-template-universal.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'HorizonOS Editor',
          detail: 'ARM64 · 174.5 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-editor-horizonos-arm64.zip`,
        },
        {
          icon: 'Smartphone',
          title: 'PICO OS Editor',
          detail: 'ARM64 · 174.0 MB',
          href: `${RELEASE_DOWNLOAD_URL}/NextEngine-v0.1.4.7.1-preview-android-editor-picoos-arm64.zip`,
        },
      ],
    },
    security: {
      eyebrow: 'Trust model',
      title: 'Conservative permissions and review boundaries',
      intro:
        'When AI participates in real development work, the important part is not unlimited power. It is knowing what it will do, when approval is required, and how to roll back.',
      items: [
        {
          icon: 'ShieldCheck',
          title: 'Permission profiles',
          text: 'Tool calls and write access are enabled by configuration, with risky actions requiring explicit approval.',
        },
        {
          icon: 'FileSearch',
          title: 'Reference checks',
          text: 'Before deleting or rewriting assets, the editor checks references to reduce accidental project damage.',
        },
        {
          icon: 'RotateCcw',
          title: 'Rollback-friendly edits',
          text: 'AI changes are surfaced through diffs, review panels, and confirmations so developers keep final control.',
        },
      ],
    },
    build: {
      eyebrow: 'Build',
      title: 'From repository to local editor',
      intro: 'For source builds, module lookup, or deeper development, this map shows where the main capabilities live.',
      commandTitle: 'Build commands',
      treeTitle: 'Source layout',
      commands: [
        '# Daily build',
        'scons platform=windows',
        '',
        '# Development build with tests',
        'scons platform=windows target=editor dev_build=yes tests=yes',
        '',
        '# Run AI-related tests',
        'bin\\next.windows.editor.dev.x86_64.console.exe --test --test-case="*[AgentV1]*"',
      ],
      tree: [
        ['editor/agent_v1/', 'Agent V1 backend: core, domain, session, runtime, tools, permission, MCP, Skill, agents, UI adapter'],
        ['editor/agent_ui/', 'AI Dock, Composer, message list, settings pages, attachment bar, and review panels'],
        ['editor/user_system/', 'Accounts, auth, server configuration, and personalized state'],
        ['core/markdown/', 'MarkdownParser / MarkdownNode core parsing layer'],
        ['scene/gui/markdown_viewer*', 'Runtime MarkdownViewer control, layout, draw, image loading, and code highlight helpers'],
        ['thirdparty/cmark/', 'CommonMark parser vendored for Markdown parsing'],
        ['tests/editor/', 'Agent V1, AI UI, and user system tests'],
        ['tests/scene/', 'MarkdownViewer runtime scene tests'],
      ],
    },
    feedback: {
      title: 'Share feedback with NEXT Engine',
      loading: 'Loading survey...',
      surveySrc: FEEDBACK_SURVEY_SRC,
    },
    footer: {
      text: 'A Godot-based AI game development editor that works with real project context.',
      links: ['Start', 'Features', 'Download', 'Trust', 'Build', 'Feedback'],
      copyright: '© 2026 Fantety. All rights reserved.',
      bilibili: 'Bilibili',
    },
  },
};
